import { Camera, CameraOff, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Tirar a foto na hora, pela webcam do computador.
 *
 * No celular e no tablet o "Tirar foto" é o `capture` do `<input type="file">`, que abre o
 * aplicativo de câmera do aparelho (`PhotoSourceButtons`). No computador o `capture` é
 * ignorado — o botão abriria o explorador de arquivos, que é o "Anexar" ao lado —, e por isso
 * aqui a câmera é aberta de verdade, com `getUserMedia`.
 *
 * O quadro é capturado na resolução nativa do vídeo e sai como JPEG. Daí em diante ele é um
 * arquivo como qualquer outro: passa pela validação por conteúdo (RN-105) e pela compressão,
 * sem caminho paralelo — uma foto tirada aqui não pode ser menos verificada do que uma
 * escolhida do disco.
 *
 * **A parada do fluxo não é detalhe.** Uma faixa de vídeo esquecida mantém a lâmpada da
 * câmera acesa e o sensor ligado depois de o diálogo fechar — no tablet isso é bateria
 * queimando no bolso de alguém, e é o defeito clássico deste tipo de tela.
 */
export interface CameraCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Recebe o arquivo capturado — daqui ele segue o mesmo caminho de um anexo escolhido. */
  onCapture: (file: File) => void;
}

type CameraState =
  | { readonly kind: 'starting' }
  | { readonly kind: 'live' }
  | { readonly kind: 'failed'; readonly message: string };

/** O que deu errado, dito de um jeito que diz o que fazer. */
function describeFailure(cause: unknown): string {
  const name = cause instanceof Error ? cause.name : '';

  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'O navegador bloqueou o acesso à câmera. Autorize a câmera para este endereço e tente de novo, ou use "Anexar".';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Nenhuma câmera encontrada neste aparelho. Use "Anexar" para enviar uma foto já tirada.';
  }
  if (name === 'NotReadableError') {
    return 'A câmera está em uso por outro aplicativo. Feche o outro aplicativo e tente de novo.';
  }
  return 'Não foi possível abrir a câmera. Use "Anexar" para enviar uma foto já tirada.';
}

export function CameraCaptureDialog({ open, onOpenChange, onCapture }: CameraCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | undefined>(undefined);
  const [state, setState] = useState<CameraState>({ kind: 'starting' });
  const [attempt, setAttempt] = useState(0);

  /** Desliga o sensor. Chamado ao fechar, ao desmontar e antes de qualquer nova tentativa. */
  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = undefined;
  }, []);

  useEffect(() => {
    if (!open) {
      stopStream();
      return undefined;
    }

    let cancelled = false;
    let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
    let cleanupVideo: (() => void) | undefined;
    setState({ kind: 'starting' });

    void (async () => {
      try {
        // `mediaDevices` não existe fora de contexto seguro (HTTPS ou localhost). Sem esta
        // guarda o acesso lançaria um TypeError sem nenhuma explicação útil.
        if (navigator.mediaDevices === undefined) {
          throw new Error('sem mediaDevices');
        }

        // `environment` é a câmera traseira: quem fotografa a frente de serviço aponta para
        // a parede, não para o próprio rosto.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });

        if (cancelled) {
          // O diálogo fechou enquanto a permissão era decidida: o fluxo já nasceu órfão e
          // precisa morrer aqui, senão a câmera fica ligada sem nenhuma tela mostrando.
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (video === null) {
          // O vídeo é mantido montado justamente para isto não acontecer. Se acontecer,
          // falhar em voz alta é melhor do que ficar girando com a câmera ligada.
          throw new Error('vídeo não montado');
        }
        video.srcObject = stream;

        /*
         * Pronta é quando **existe quadro**, não quando a permissão volta.
         *
         * Entre uma coisa e outra o vídeo ainda mede 0×0, e capturar aí desenha um canvas
         * de largura zero: o `toBlob` devolve nada, nenhum anexo é criado e o diálogo fica
         * aberto sem dizer por quê. Um toque rápido no tablet cai exatamente nessa janela,
         * e a falha seria silenciosa — a pior forma de falhar.
         */
        const markLive = () => {
          if (!cancelled) setState({ kind: 'live' });
        };
        if (video.videoWidth > 0) markLive();
        else video.addEventListener('loadedmetadata', markLive, { once: true });
        cleanupVideo = () => {
          video.removeEventListener('loadedmetadata', markLive);
        };

        // Câmera que abre e nunca entrega imagem existe — câmera virtual, driver travado.
        // Sem este limite a tela ficaria em "Abrindo a câmera…" para sempre.
        timeout = globalThis.setTimeout(() => {
          if (cancelled) return;
          setState((current) =>
            current.kind === 'starting'
              ? {
                  kind: 'failed',
                  message: 'A câmera abriu mas não enviou imagem. Tente de novo, ou use "Anexar".',
                }
              : current,
          );
        }, 10_000);
      } catch (cause) {
        if (!cancelled) setState({ kind: 'failed', message: describeFailure(cause) });
      }
    })();

    return () => {
      cancelled = true;
      if (timeout !== undefined) globalThis.clearTimeout(timeout);
      cleanupVideo?.();
      stopStream();
    };
  }, [open, attempt, stopStream]);

  // Desmontar sem passar por `open: false` — troca de rota, por exemplo — também desliga.
  useEffect(() => stopStream, [stopStream]);

  const capture = () => {
    const video = videoRef.current;
    // Segunda camada da mesma regra: o botão já fica desabilitado sem quadro, e capturar
    // 0×0 produziria um arquivo vazio em silêncio.
    if (video === null || state.kind !== 'live' || video.videoWidth === 0) return;

    // A resolução nativa do vídeo, não a da caixa na tela: o quadro exibido é menor do que
    // o que o sensor entrega, e capturar o tamanho exibido jogaria fora a nitidez que a
    // foto precisa ter para servir de evidência.
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context === null) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob === null) return;
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        onCapture(new File([blob], `conferencia-${stamp}.jpg`, { type: 'image/jpeg' }));
        onOpenChange(false);
      },
      'image/jpeg',
      // Alta na captura porque a compressão do anexo vem depois: reduzir duas vezes
      // acumularia perda sem ganho de espaço.
      0.92,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Tirar foto</DialogTitle>
          <DialogDescription>
            A foto entra direto na conferência, com autor e instante.
          </DialogDescription>
        </DialogHeader>

        {state.kind === 'failed' && (
          <div
            role="alert"
            className="flex flex-col items-center gap-3 rounded-(--radius-card) border border-(--color-state-warn) p-6 text-center"
          >
            <CameraOff className="size-8 text-(--color-state-warn)" aria-hidden="true" />
            <p className="text-sm">{state.message}</p>
            <Button
              variant="secondary"
              onClick={() => {
                setAttempt((value) => value + 1);
              }}
            >
              <RefreshCw aria-hidden="true" />
              Tentar de novo
            </Button>
          </div>
        )}

        {/*
          O vídeo fica **sempre montado** enquanto o diálogo existe, e apenas escondido na
          falha. Desmontá-lo criaria uma corrida no "tentar de novo": a permissão volta
          antes de o React repintar o elemento, `videoRef` ainda é nulo, e o fluxo não teria
          onde ser ligado — a tela ficaria presa em "Abrindo a câmera…" com a câmera aberta.
        */}
        <div
          className={
            state.kind === 'failed' ? 'hidden' : 'overflow-hidden rounded-(--radius-card) bg-black'
          }
        >
          {/*
            `playsInline` é o que impede o iOS de abrir o vídeo em tela cheia própria,
            engolindo o diálogo; `muted` é exigido para o autoplay valer sem gesto.
          */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="max-h-[60vh] w-full object-contain"
          />
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            className="min-h-11"
            disabled={state.kind !== 'live'}
            onClick={capture}
          >
            <Camera aria-hidden="true" />
            {state.kind === 'starting' ? 'Abrindo a câmera…' : 'Tirar foto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
